// src/salesAssessment/SalesAssessmentComparison.js
import { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  AlertTitle,
} from '@mui/material';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';
import SalesAssessmentComparisonSetup from './SalesAssessmentComparisonSetup';
import SalesAssessmentComparisonResults from './SalesAssessmentComparisonResults';

export default function SalesAssessmentComparison() {
  const { authToken, showAlert, project, organization, language } = useStore.getState();
  const { t } = useTranslation();

  // Estados de datos
  const [positions, setPositions] = useState([]);
  const [targetPosition, setTargetPosition] = useState(null);
  const [salesTests, setSalesTests] = useState([]);
  const [selectedTests, setSelectedTests] = useState({});

  const [loading, setLoading] = useState({
    positions: false,
    tests: false,
    creating: false,
  });

  const [createError, setCreateError] = useState(null);

  // Estados de navegación
  const [currentStep, setCurrentStep] = useState('setup'); // 'setup' | 'results'
  const [currentComparison, setCurrentComparison] = useState(null);

  useEffect(() => {
    if (project && authToken) {
      loadPositions();
      loadSalesTests();
    }
  }, [project, authToken]);

  const loadPositions = async () => {
    try {
      setLoading(prev => ({ ...prev, positions: true }));
      const { data } = await api.get('/positions', {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (data.response && data.response.status_response === 'OK') {
        setPositions(data.response.data || []);
      } else {
        setPositions([]);
      }
    } catch (error) {
      console.error('Error al cargar posiciones:', error);
      setPositions([]);
    } finally {
      setLoading(prev => ({ ...prev, positions: false }));
    }
  };

  const loadSalesTests = async () => {
    try {
      setLoading(prev => ({ ...prev, tests: true }));
      const { data } = await api.get('/sales-assessments/by-project', {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (data.response && data.response.status_response === 'OK') {
        const evaluated = (data.response.data || []).filter(
          test => test.status === 'evaluated'
        );
        setSalesTests(evaluated);
      } else {
        setSalesTests([]);
      }
    } catch (error) {
      console.error('Error al cargar sales assessments:', error);
      showAlert(t('salesAssessment.comparison.testsLoadError'), 'error');
      setSalesTests([]);
    } finally {
      setLoading(prev => ({ ...prev, tests: false }));
    }
  };

  const handleTargetPositionChange = (newPosition) => {
    if (targetPosition?.uid !== newPosition?.uid) {
      setSelectedTests({});
    }
    setTargetPosition(newPosition);
  };

  const handleTestSelection = (testId) => {
    setSelectedTests(prev => {
      const updated = { ...prev };
      if (updated[testId]) {
        delete updated[testId];
      } else {
        updated[testId] = true;
      }
      return updated;
    });
  };

  const canStartComparison = () => {
    return Object.keys(selectedTests).filter(id => selectedTests[id]).length >= 2;
  };

  const handleStartComparison = async () => {
    if (!canStartComparison()) {
      showAlert(t('salesAssessment.comparison.selectAtLeastTwo'), 'warning');
      return;
    }

    try {
      setLoading(prev => ({ ...prev, creating: true }));
      setCreateError(null);

      const { data } = await api.post(
        '/sales-assessment-comparisons',
        {
          project_id: project,
          organization_id: organization,
          position_id: targetPosition?.uid || null,
          position_name: targetPosition?.name || null,
          selected_tests: selectedTests,
          language: language || 'es',
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (data.response && data.response.status_response === 'OK') {
        showAlert(t('salesAssessment.comparison.createdSuccess'), 'success');
        setCurrentComparison({ id: data.response.data.id });
        setCurrentStep('results');
      } else {
        throw new Error(data.response?.message || 'Error desconocido');
      }
    } catch (error) {
      console.error('Error al crear comparación:', error);
      const msg = error.response?.data?.response?.message || error.message;
      setCreateError(msg);
      showAlert(t('salesAssessment.comparison.createError'), 'error');
    } finally {
      setLoading(prev => ({ ...prev, creating: false }));
    }
  };

  const handleBackToSetup = () => {
    setCurrentStep('setup');
    setCurrentComparison(null);
    setSelectedTests({});
    setTargetPosition(null);
    setCreateError(null);
  };

  // Vista de resultados
  if (currentStep === 'results' && currentComparison) {
    return (
      <SalesAssessmentComparisonResults
        comparisonId={currentComparison.id}
        onBack={handleBackToSetup}
      />
    );
  }

  // Vista de setup
  return (
    <Box>
      {createError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError(null)}>
          <AlertTitle>{t('error')}</AlertTitle>
          {createError}
        </Alert>
      )}
      <SalesAssessmentComparisonSetup
        positions={positions}
        targetPosition={targetPosition}
        salesTests={salesTests}
        selectedTests={selectedTests}
        loading={loading}
        onTargetPositionChange={handleTargetPositionChange}
        onTestSelection={handleTestSelection}
        onStartComparison={handleStartComparison}
        canStart={canStartComparison()}
      />
    </Box>
  );
}